import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

// GET /api/settings/backup-database/list
export const fetchBackupListThunk = createAsyncThunk(
  "backupDatabase/fetchList",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/settings/backup-database/list");
      if (!res.data?.success) {
        return rejectWithValue(res.data?.message || "Failed");
      }
      return res.data?.data || [];
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message || e.message || "Server error"
      );
    }
  }
);

// POST /api/settings/backup-database
export const takeBackupThunk = createAsyncThunk(
  "backupDatabase/takeBackup",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/api/settings/backup-database");
      if (!res.data?.success) {
        return rejectWithValue(res.data?.message || "Failed");
      }

      dispatch(fetchBackupListThunk());
      return res.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message || e.message || "Server error"
      );
    }
  }
);

// GET /api/settings/backup-database/download/:fileName
export const downloadBackupThunk = createAsyncThunk(
  "backupDatabase/download",
  async (fileName, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/api/settings/backup-database/download/${encodeURIComponent(fileName)}`,
        {
          responseType: "blob",
        }
      );

      return {
        data: res.data,
        fileName,
      };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message || e.message || "Download failed"
      );
    }
  }
);

const slice = createSlice({
  name: "backupDatabase",
  initialState: {
    list: [],
    loading: false,
    takingBackup: false,
    downloading: null,
    error: null,
    success: null,
  },
  reducers: {
    clearBackupMsg(state) {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (b) => {
    // fetch backup list
    b.addCase(fetchBackupListThunk.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchBackupListThunk.fulfilled, (s, a) => {
      s.loading = false;
      s.list = Array.isArray(a.payload) ? a.payload : [];
    });
    b.addCase(fetchBackupListThunk.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload || "Failed";
    });

    // take backup
    b.addCase(takeBackupThunk.pending, (s) => {
      s.takingBackup = true;
      s.error = null;
      s.success = null;
    });
    b.addCase(takeBackupThunk.fulfilled, (s, a) => {
      s.takingBackup = false;
      s.success = a.payload?.message || "Backup created successfully";
    });
    b.addCase(takeBackupThunk.rejected, (s, a) => {
      s.takingBackup = false;
      s.error = a.payload || "Failed";
    });

    // download backup
    b.addCase(downloadBackupThunk.pending, (s, a) => {
      s.downloading = a.meta.arg;
      s.error = null;
    });
    b.addCase(downloadBackupThunk.fulfilled, (s, a) => {
      s.downloading = null;

      const { data, fileName } = a.payload || {};
      if (!data || !fileName) return;

      const blob = new Blob([data], { type: "application/sql" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    });
    b.addCase(downloadBackupThunk.rejected, (s, a) => {
      s.downloading = null;
      s.error = a.payload || "Download failed";
    });
  },
});

export const { clearBackupMsg } = slice.actions;
export default slice.reducer;